<template>
  <v-container fluid>
    <v-card>
      <v-card-title>
        Food Prep Team Assignment
        <v-spacer></v-spacer>
        <v-autocomplete
          v-model="selectedFood"
          :items="foods"
          item-text="fooddescriptionsimple"
          item-value="foodid"
          label="Select Food"
          return-object
          @change="loadMealFoodItems"
          :loading="loadingFoods"
          no-data-text="No foods found"
          clearable
          :search-input.sync="searchText"
          @update:search-input="onFoodInput"
        >
          <template v-slot:selection="{ item }">
            {{ item.fooddescriptionsimple || item.fooddescription }}
          </template>
          <template v-slot:item="{ item }">
            <v-list-item-content>
              <v-list-item-title>{{ item.fooddescriptionsimple || item.fooddescription }}</v-list-item-title>
              <v-list-item-subtitle>ID: {{ item.foodid }}</v-list-item-subtitle>
            </v-list-item-content>
          </template>
        </v-autocomplete>
      </v-card-title>

      <v-card-actions class="mt-4">
        <v-row>
          <v-col cols="12">
            <v-expansion-panels v-if="selected.length > 0">
              <v-expansion-panel>
                <v-expansion-panel-header>
                  <v-chip class="mr-2" color="primary">{{ selected.length }} items selected</v-chip>
                  <span>Bulk Actions</span>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-row class="mb-4">
                    <v-col cols="12" md="3">
                      <v-select
                        v-model="bulkTeam"
                        :items="defaultTeams"
                        clearable
                        label="Set Team for Selected"
                        :disabled="selected.length === 0"
                      ></v-select>
                    </v-col>
                    <v-col cols="12" md="2">
                      <v-btn
                        color="primary"
                        block
                        @click="applyTeamToSelected"
                        :disabled="!bulkTeam || selected.length === 0 || loading"
                        :loading="loading"
                      >
                        Apply Team
                      </v-btn>
                    </v-col>
                    <v-col cols="12">
                      <v-textarea
                        v-model="bulkInstruction"
                        rows="2"
                        auto-grow
                        label="Set Instructions for Selected"
                        :disabled="selected.length === 0"
                      ></v-textarea>
                    </v-col>
                    <v-col cols="12" md="2">
                      <v-btn
                        color="primary"
                        block
                        @click="applyInstructionsToSelected"
                        :disabled="!bulkInstruction || selected.length === 0 || loading"
                        :loading="loading"
                      >
                        Apply Instructions
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-col>
        </v-row>

      </v-card-actions>
      <v-card-text>
        <v-alert
          v-if="selectedFood && mealFoodItems.length === 0 && !loadingMealFoodItems"
          type="info"
          class="mt-4"
        >
          No meal items found using this food
        </v-alert>

        <div v-if="selectedFood && mealFoodItems.length > 0">
          <h3 class="mt-4">Prep Assignments for {{ selectedFood.description }}</h3>
          <v-data-table
            v-model="selected"
            :headers="headers"
            :items="mealFoodItems"
            :loading="loadingMealFoodItems"
            :items-per-page="15"
            multi-sort
            class="elevation-1 mt-4"
            show-select
            item-key="id"
          >
            <template v-slot:item.mealName="{ item }">
              <router-link :to="{ name: 'MealDetail', params: { date: item.meal_date, id: item.meal_id }}">
                {{ item.mealName }}
              </router-link>
            </template>

            <template v-slot:item.componentName="{ item }">
              <span v-if="item.componentName">{{ item.componentName }}</span>
              <span v-else class="grey--text">No component</span>
            </template>

            <template v-slot:item.team="{ item }">
              <v-select
                v-model="item.team"
                :items="defaultTeams"
                clearable
                label="Team"
                dense
                @change="saveTeamAssignment(item)"
                :loading="item.saving"
                :disabled="isDateInPast(item.meal_date)"
                :hint="isDateInPast(item.meal_date) ? 'Past dates cannot be updated' : ''"
                persistent-hint
              ></v-select>
            </template>

            <template v-slot:item.instruction="{ item }">
              <v-textarea
                v-model="item.instruction"
                rows="2"
                auto-grow
                dense
                hide-details
                label="Prep instructions"
                @change="saveInstructionAssignment(item)"
                :loading="item.saving"
                :disabled="isDateInPast(item.meal_date)"
              ></v-textarea>
            </template>

            <template v-slot:item.actions="{ item }">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    color="primary"
                    v-bind="attrs"
                    v-on="on"
                    @click="applyToAll(item)"
                    :disabled="loading || isDateInPast(item.meal_date)"
                  >
                    <v-icon>mdi-content-copy</v-icon>
                  </v-btn>
                </template>
                <span v-if="isDateInPast(item.meal_date)">Cannot update past dates</span>
                <span v-else>Apply this prep assignment to all instances</span>
              </v-tooltip>
            </template>
          </v-data-table>


        </div>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import {mapState, mapActions} from 'vuex';
import moment from 'moment';
import api from '@/api';

export default {
  name: 'MealFoodPrepAssignment',
  data() {
    return {
      searchText: '',
      foods: [],
      selectedFood: null,
      mealFoodItems: [],
      loadingFoods: false,
      loadingMealFoodItems: false,
      loading: false,
      selectedTeam: null,
      searchTimeout: null,
      selected: [],
      bulkTeam: null,
      bulkInstruction: '',
      headers: [
        {text: 'Meal', value: 'mealName', sortable: true},
        {text: 'Date', value: 'meal_date', sortable: true},
        {text: 'Component', value: 'componentName', sortable: true},
        {text: 'Team', value: 'team', sortable: false},
        {text: 'Prep Instructions', value: 'instruction', sortable: false},
        {text: 'Actions', value: 'actions', sortable: false}
      ]
    };
  },
  computed: {
    ...mapState(['defaultTeams']),
    today() {
      return moment().startOf('day');
    }
  },
  mounted() {
    this.loadInitialFoods();
    this.checkUrlParams();
  },
  watch: {
    // Watch for selected food changes to update URL
    selectedFood() {
      this.syncToUrl();
    },
    // Watch for URL changes
    '$route.query'() {
      this.checkUrlParams();
    }
  },
  methods: {
    // Sync selected food to URL
    syncToUrl() {
      if (this.selectedFood) {
        // Update URL with selected food ID
        this.$router.replace({
          query: {
            ...this.$route.query,
            foodId: this.selectedFood.foodid
          }
        }).catch(err => {
          // Ignore navigation duplicates
          if (err.name !== 'NavigationDuplicated') {
            throw err;
          }
        });
      } else {
        // Remove food ID from URL if no food is selected
        const query = { ...this.$route.query };
        delete query.foodId;
        this.$router.replace({ query }).catch(err => {
          if (err.name !== 'NavigationDuplicated') {
            throw err;
          }
        });
      }
    },

    // Check URL params for food ID and load data if necessary
    async checkUrlParams() {
      const foodId = this.$route.query.foodId;
      if (foodId && (!this.selectedFood || this.selectedFood.foodid !== foodId)) {
        // Load initial foods to ensure we have data
        if (this.foods.length === 0) {
          await this.loadInitialFoods();
        }

        // Try to find the food in our loaded foods
        let foundFood = this.foods.find(food => food.foodid === parseInt(foodId) || food.foodid === foodId);

        // If not found, try to load directly by ID
        if (!foundFood) {
          try {
            // Use a direct API call to get the food
            const { data } = await api.get(`v2/food/${foodId}`);
            if (data) {
              foundFood = {
                ...data,
                id: data.id,
                foodid: data.id,
                description: data.fooddescriptionsimple || data.fooddescription
              };
            }
          } catch (error) {
            console.error('Failed to load food by ID', error);
          }
        }

        // Set the selected food if found
        if (foundFood) {
          this.selectedFood = foundFood;
          this.loadMealFoodItems();
        }
      }
    },

    isDateInPast(dateString) {
      if (!dateString) return false;
      const itemDate = moment(dateString).startOf('day');
      return itemDate.isBefore(this.today);
    },
    ...mapActions({
      searchFoodsAction: 'searchFoods',
      fetchFoodMealItems: 'fetchFoodMealItems',
      savePrepStep: 'savePrepStep',
      applyPrepToAllFoodItems: 'applyPrepToAllFoodItems',
      applyTeamToAllFoodItems: 'applyTeamToAllFoodItems'
    }),

    async loadInitialFoods() {
      this.loadingFoods = true;
      try {
        // Load initial list of foods that are used in meals
        this.foods = await this.searchFoodsAction({query: ''});
      } catch (error) {
        console.error('Failed to load initial foods', error);
        this.$root.$emit('showMessage', {
          message: 'Failed to load foods',
          color: 'error'
        });
      } finally {
        this.loadingFoods = false;
      }
    },

    onFoodInput(val) {
      // Clear previous timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      if (!val) return;

      // Don't search if the input is too short
      if (typeof val === 'string' && val.length < 2) return;

      // Set a new timeout to debounce the search
      this.searchTimeout = setTimeout(async () => {
        this.loadingFoods = true;
        try {
          this.foods = await this.searchFoodsAction({query: val});
        } catch (error) {
          console.error('Failed to search foods', error);
          this.$root.$emit('showMessage', {
            message: 'Failed to search foods',
            color: 'error'
          });
        } finally {
          this.loadingFoods = false;
        }
      }, 300); // 300ms debounce
    },

    async loadMealFoodItems() {
      if (!this.selectedFood) {
        this.mealFoodItems = [];
        return;
      }

      this.loadingMealFoodItems = true;
      try {
        const items = await this.fetchFoodMealItems({foodid: this.selectedFood.foodid});

        // Add the saving flag and store original values to each item
        this.mealFoodItems = items.map(item => ({
          ...item,
          saving: false,
          _originalTeam: item.team,
          _originalInstruction: item.instruction
        }));
      } catch (error) {
        console.error('Failed to load meal food items', error);
        this.$root.$emit('showMessage', {
          message: 'Failed to load meal food items',
          color: 'error'
        });
      } finally {
        this.loadingMealFoodItems = false;
      }
    },

    async saveTeamAssignment(item) {
      // Check if date is in the past
      if (this.isDateInPast(item.meal_date)) {
        this.$root.$emit('showMessage', {
          message: 'Cannot update assignments for past dates',
          color: 'error',
          timeout: 3000
        });
        // Reset to previous value (before the attempted change)
        this.$nextTick(() => {
          item.team = item._originalTeam || null;
        });
        return;
      }

      // Store original value in case we need to revert
      item._originalTeam = item.team;

      item.saving = true;
      try {
        const result = await this.savePrepStep({
          mealFoodId: item.id,
          team: item.team,
          instruction: item.instruction || '',
          stepId: item.step_id
        });

        // Update the step_id if it's a new step
        if (!item.step_id) {
          item.step_id = result.id;
        }

        this.$root.$emit('showMessage', {
          message: 'Team assignment saved',
          color: 'success',
          timeout: 2000
        });
      } catch (error) {
        console.error('Failed to save team assignment', error);
        this.$root.$emit('showMessage', {
          message: 'Failed to save team assignment',
          color: 'error'
        });
      } finally {
        item.saving = false;
      }
    },

    async saveInstructionAssignment(item) {
      // Check if date is in the past
      if (this.isDateInPast(item.meal_date)) {
        this.$root.$emit('showMessage', {
          message: 'Cannot update instructions for past dates',
          color: 'error',
          timeout: 3000
        });
        // Reset to previous value
        this.$nextTick(() => {
          item.instruction = item._originalInstruction || '';
        });
        return;
      }

      // Store original value in case we need to revert
      item._originalInstruction = item.instruction;

      item.saving = true;
      try {
        const result = await this.savePrepStep({
          mealFoodId: item.id,
          team: item.team || null,
          instruction: item.instruction,
          stepId: item.step_id
        });

        // Update the step_id if it's a new step
        if (!item.step_id) {
          item.step_id = result.id;
        }

        this.$root.$emit('showMessage', {
          message: 'Instruction saved',
          color: 'success',
          timeout: 2000
        });
      } catch (error) {
        console.error('Failed to save instruction', error);
        this.$root.$emit('showMessage', {
          message: 'Failed to save instruction',
          color: 'error'
        });
      } finally {
        item.saving = false;
      }
    },

    async applyToAll(sourceItem) {
      if (!confirm(`Apply this prep assignment to all instances of ${this.selectedFood.fooddescriptionsimple || this.selectedFood.fooddescription}?`)) {
        return;
      }

      this.loading = true;
      try {
        await this.applyPrepToAllFoodItems({
          foodId: this.selectedFood.foodid,
          team: sourceItem.team,
          instruction: sourceItem.instruction
        });

        await this.loadMealFoodItems();
        this.$root.$emit('showMessage', {
          message: 'Applied prep assignment to all instances',
          color: 'success',
          timeout: 3000
        });
      } catch (error) {
        console.error('Failed to apply to all', error);
        this.$root.$emit('showMessage', {
          message: 'Failed to apply to all instances',
          color: 'error'
        });
      } finally {
        this.loading = false;
      }
    },

    async applyTeamToAll() {
      if (!this.selectedTeam || !confirm(`Apply team "${this.selectedTeam}" to all instances of ${this.selectedFood.fooddescriptionsimple || this.selectedFood.fooddescription}?`)) {
        return;
      }

      this.loading = true;
      try {
        await this.applyTeamToAllFoodItems({
          foodId: this.selectedFood.foodid,
          team: this.selectedTeam
        });

        // Update all items in the local list with the new team
        this.mealFoodItems.forEach(item => {
          item.team = this.selectedTeam;
        });

        this.$root.$emit('showMessage', {
          message: `Applied team "${this.selectedTeam}" to all instances`,
          color: 'success',
          timeout: 3000
        });
      } catch (error) {
        console.error('Failed to apply team to all', error);
        this.$root.$emit('showMessage', {
          message: 'Failed to apply team to all instances',
          color: 'error'
        });
      } finally {
        this.loading = false;
        this.selectedTeam = null;
      }
    },

    async applyTeamToSelected() {
      if (!this.bulkTeam || this.selected.length === 0) {
        return;
      }

      // Filter out items with past dates
      const validItems = this.selected.filter(item => !this.isDateInPast(item.meal_date));
      const pastItems = this.selected.filter(item => this.isDateInPast(item.meal_date));

      if (validItems.length === 0) {
        this.$root.$emit('showMessage', {
          message: 'Cannot update assignments for past dates',
          color: 'error',
          timeout: 3000
        });
        return;
      }

      // Show confirmation message with count of valid items
      if (!confirm(`Apply team "${this.bulkTeam}" to ${validItems.length} selected items?${pastItems.length ? `\n\nNote: ${pastItems.length} items with past dates will be skipped.` : ''}`)) {
        return;
      }

      this.loading = true;
      try {
        // Create an array of promises for each valid selected item
        const savePromises = validItems.map(item => {
          // Apply the team to the item
          return this.savePrepStep({
            mealFoodId: item.id,
            team: this.bulkTeam,
            instruction: item.instruction || '',
            stepId: item.step_id
          });
        });

        // Wait for all promises to resolve
        await Promise.all(savePromises);

        // Update local data
        validItems.forEach(selectedItem => {
          const item = this.mealFoodItems.find(i => i.id === selectedItem.id);
          if (item) {
            item.team = this.bulkTeam;
          }
        });

        let message = `Applied team "${this.bulkTeam}" to ${validItems.length} selected items`;
        if (pastItems.length > 0) {
          message += ` (${pastItems.length} past date items were skipped)`;
        }

        this.$root.$emit('showMessage', {
          message,
          color: 'success',
          timeout: 3000
        });

        // Clear the bulk team
        this.bulkTeam = null;
      } catch (error) {
        console.error('Failed to apply team to selected items', error);
        this.$root.$emit('showMessage', {
          message: 'Failed to apply team to selected items',
          color: 'error'
        });
      } finally {
        this.loading = false;
      }
    },

    async applyInstructionsToSelected() {
      if (!this.bulkInstruction || this.selected.length === 0) {
        return;
      }

      // Filter out items with past dates
      const validItems = this.selected.filter(item => !this.isDateInPast(item.meal_date));
      const pastItems = this.selected.filter(item => this.isDateInPast(item.meal_date));

      if (validItems.length === 0) {
        this.$root.$emit('showMessage', {
          message: 'Cannot update instructions for past dates',
          color: 'error',
          timeout: 3000
        });
        return;
      }

      // Show confirmation message with count of valid items
      if (!confirm(`Apply these instructions to ${validItems.length} selected items?${pastItems.length ? `\n\nNote: ${pastItems.length} items with past dates will be skipped.` : ''}`)) {
        return;
      }

      this.loading = true;
      try {
        // Create an array of promises for each valid selected item
        const savePromises = validItems.map(item => {
          return this.savePrepStep({
            mealFoodId: item.id,
            team: item.team,
            instruction: this.bulkInstruction,
            stepId: item.step_id
          });
        });

        // Wait for all promises to resolve
        await Promise.all(savePromises);

        // Update local data
        validItems.forEach(selectedItem => {
          const item = this.mealFoodItems.find(i => i.id === selectedItem.id);
          if (item) {
            item.instruction = this.bulkInstruction;
          }
        });

        let message = `Applied instructions to ${validItems.length} selected items`;
        if (pastItems.length > 0) {
          message += ` (${pastItems.length} past date items were skipped)`;
        }

        this.$root.$emit('showMessage', {
          message,
          color: 'success',
          timeout: 3000
        });

        // Clear the bulk instruction
        this.bulkInstruction = '';
      } catch (error) {
        console.error('Failed to apply instructions to selected items', error);
        this.$root.$emit('showMessage', {
          message: 'Failed to apply instructions to selected items',
          color: 'error'
        });
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>